import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ClipboardList, MapPin, Calendar, Phone, Loader2, Filter } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new_booking: '#94A3B8', confirmed: '#3B82F6', driver_assigned: '#6366F1',
  on_site: '#F59E0B', collected: '#22C55E', completed: '#16A34A',
  on_hold: '#EF4444', cancelled: '#6B7280', collection_in_progress: '#F97316',
};

const DriverJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('driver_token');
        const result = await safeFetch(`${API_URL}/api/go/jobs?status=${filter}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (result.ok) setJobs(result.data.jobs || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="driver-jobs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Jobs</h1>
          <p className="text-[#64748B] text-sm mt-1">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          {['active', 'completed', 'all'].map(f => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              className={filter === f
                ? 'bg-[#10B981] hover:bg-[#059669] text-white border-0'
                : 'border-[#334155] text-[#94A3B8] hover:text-white hover:bg-[#334155] bg-transparent'}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>
      ) : jobs.length === 0 ? (
        <Card className="border-0 bg-[#1E293B]">
          <CardContent className="py-16 text-center">
            <ClipboardList className="w-12 h-12 text-[#334155] mx-auto mb-3" />
            <p className="text-[#64748B]">No {filter !== 'all' ? filter : ''} jobs found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map(job => {
            const addr = job.waste_details?.site_address || job.delivery_details?.delivery_address || '';
            const customer = job.waste_details?.customer_name || job.client_name || '';
            const customerMobile = job.waste_details?.customer_mobile || '';

            return (
              <Card key={job.id} className="border-0 bg-[#1E293B] hover:bg-[#1E293B]/80 transition-colors" data-testid={`job-${job.booking_ref}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[job.internal_status] || '#94A3B8' }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-white">{job.booking_ref}</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                            backgroundColor: (STATUS_COLORS[job.internal_status] || '#94A3B8') + '20',
                            color: STATUS_COLORS[job.internal_status] || '#94A3B8'
                          }}>
                            {(job.internal_status || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        {job.scheduled_date && (
                          <span className="text-xs text-[#64748B] flex items-center gap-1 flex-shrink-0">
                            <Calendar className="w-3 h-3" />
                            {job.scheduled_date}
                          </span>
                        )}
                      </div>

                      {addr && (
                        <p className="text-sm text-[#94A3B8] flex items-center gap-1.5 mb-1.5">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-[#64748B]" />
                          <span className="truncate">{addr}</span>
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-[#64748B]">
                        {customer && <span>{customer}</span>}
                        {customerMobile && (
                          <a href={`tel:${customerMobile}`} className="flex items-center gap-1 text-[#10B981] hover:underline">
                            <Phone className="w-3 h-3" />{customerMobile}
                          </a>
                        )}
                        {job.waste_details?.waste_type && (
                          <span className="px-1.5 py-0.5 rounded bg-[#334155] text-[#94A3B8]">{job.waste_details.waste_type}</span>
                        )}
                      </div>

                      {job.ops_notes && (
                        <p className="text-xs text-yellow-500/80 mt-2 bg-yellow-500/5 px-2 py-1 rounded">
                          {job.ops_notes}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DriverJobs;
